// silent-sso.ts
// Signs the user in silently when they already have a Microsoft session from another PBS app.
// Works with several Microsoft accounts in the browser, thanks to the shared login_hint cookie.
//
// Company-standard drop-in (Eli / Nikola, 2026-10). MySiteV2 adaptations, all marked below:
//   1. embedded-app and /logout routes never attempt (host webviews are top-level documents).
//   2. redirectStartPage carries the hash route through the round trip (HashRouter).
//   3. signOut forwards an optional EndSessionRequest (header keeps its postLogoutRedirectUri).

import type {
  EndSessionRequest,
  IPublicClientApplication,
} from "@azure/msal-browser";

// Same name in every PBS app, otherwise the apps do not share the hint.
const HINT_COOKIE = "pbs_login_hint";
const SILENT_FLAG = "msalSilentSsoAttempted";

// The cookie lives on .teampbs.com, so every *.teampbs.com app reads it.
// On localhost / *.vercel.app the browser rejects that domain, so we leave it out there.
function cookieAttrs(): string {
  const onTeamPbs = window.location.hostname.endsWith("teampbs.com");
  return `; path=/; secure; samesite=lax${onTeamPbs ? "; domain=.teampbs.com" : ""}`;
}

function readLoginHint(): string | undefined {
  const entry = document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${HINT_COOKIE}=`));
  return entry
    ? decodeURIComponent(entry.slice(HINT_COOKIE.length + 1))
    : undefined;
}

// MySiteV2 adaptation 1: routes that must never leave for Microsoft on load.
// - #/embedded-app/* runs inside PBS Live hosts. Desktop/mobile webviews load it as the
//   TOP-LEVEL document, so the window.self !== window.top guard does not cover them, and the
//   Openings/Notifications views ride the host cookie session with no MSAL account at all.
//   Their own gates (EmbeddedAuthGate / EmbeddedMsalSsoGate) own auth there.
// - #/logout is the post-sign-out landing page; an attempt there is a guaranteed
//   login_required round trip because signOut just cleared the hint.
function isSilentSsoExcludedRoute(): boolean {
  const hash = window.location.hash;
  return hash.startsWith("#/embedded-app/") || hash.startsWith("#/logout");
}

// Call once the app has a signed-in account. Lets the next silent sign-in
// (here or in another PBS app) tell Microsoft which account to use.
export function rememberAccount(instance: IPublicClientApplication): void {
  const username = instance.getAllAccounts()[0]?.username;
  if (username) {
    document.cookie = `${HINT_COOKIE}=${encodeURIComponent(username)}; max-age=15552000${cookieAttrs()}`; // 180 days
  }
}

// Clears the hint, so the next silent attempt does not target an account that just signed out.
// MySiteV2 adaptation 3: the optional request keeps the caller's postLogoutRedirectUri.
export async function signOut(
  instance: IPublicClientApplication,
  request?: EndSessionRequest,
): Promise<void> {
  document.cookie = `${HINT_COOKIE}=; max-age=0${cookieAttrs()}`;
  await instance.logoutRedirect(request);
}

// Returns true when the page is redirecting to Microsoft (render nothing).
// Returns false when the app should render normally (signed in, or show /login).
export async function trySilentSso(
  instance: IPublicClientApplication,
  scopes: string[],
): Promise<boolean> {
  // Handles the reply from Microsoft. "login_required" / "interaction_required"
  // throws here: it means "not signed in", not an error page.
  try {
    await instance.handleRedirectPromise();
  } catch {
    // No account below means the /login page shows.
  }

  if (instance.getAllAccounts().length > 0) return false; // already signed in
  if (window.self !== window.top) return false; // Microsoft refuses to render inside a frame
  if (isSilentSsoExcludedRoute()) return false; // MySiteV2 adaptation 1

  // Once per tab. Set the flag BEFORE redirecting: otherwise a "login_required"
  // reply comes back, tries again, and loops forever.
  try {
    if (sessionStorage.getItem(SILENT_FLAG)) return false;
    sessionStorage.setItem(SILENT_FLAG, "1");
  } catch {
    return false; // no storage = no loop protection = no attempt
  }

  // Full-page redirect, not ssoSilent: ssoSilent uses a hidden iframe, which fails
  // when the browser blocks third-party cookies, and can hang up to 10 s.
  // prompt "none" = Microsoft shows nothing: returns the user signed in, or replies login_required.
  // loginHint = which account to use; without it, 2+ Microsoft accounts fail with AADSTS16000.
  // MySiteV2 adaptation 2: redirectStartPage = the current hash route, same as the /login page,
  // so a deep link survives the hash-less default redirect URI under HashRouter.
  try {
    await instance.loginRedirect({
      scopes,
      prompt: "none",
      loginHint: readLoginHint(),
      redirectStartPage: window.location.href,
    });
    return true;
  } catch {
    return false;
  }
}
